import { deepFreeze, extent } from '@/helpers'

import tileStore from './tiles'

export default {
  namespaced: true,
  state: {
    equipmentRevision: null,
    equipment: [],
    markersRevision: null,
    markers: [],
    activitiesRevision: null,
    activities: [],
    fieldsRevision: null,
    fields: [],
    mapRevision: null,
    map: {},
    campaignsRevision: null,
    campaigns: []
  },
  mutations: {
    clear (state) {
      state.equipmentRevision = null
      state.equipment = []
      state.markersRevision = null
      state.markers = []
      state.activitiesRevision = null
      state.activities = []
      state.fieldsRevision = null
      state.fields = []
      state.campaignsRevision = null
      state.campaigns = []
    },
    setEquipment (state, { revision, data }) {
      state.equipmentRevision = Number(revision)
      state.equipment = deepFreeze(data)
    },
    setActivities (state, { revision, data }) {
      state.activitiesRevision = Number(revision)
      state.activities = deepFreeze(data)
    },
    setMarkers (state, { revision, data }) {
      state.markersRevision = Number(revision)
      state.markers = deepFreeze(data)
    },
    setFields (state, { revision, data }) {
      state.fieldsRevision = Number(revision)
      state.fields = deepFreeze(data.map(enrichField))
    },
    setCampaigns (state, { revision, data }) {
      state.campaignsRevision = Number(revision)
      state.campaigns = deepFreeze(data)
    },
    updateCampaign (state, campaign) {
      const newList = state.campaigns.slice()
      const index = state.campaigns.findIndex(d => d._id === campaign._id)
      if (index < 0) {
        newList.push(deepFreeze(campaign))
      } else {
        newList.splice(index, 1, deepFreeze(campaign))
      }

      state.campaigns = Object.freeze(newList)
    },
    clearMap (state) {
      state.mapRevision = null
      state.map = {}
    },
    setMap (state, { revision, data }) {
      state.mapRevision = Number(revision)
      state.map = deepFreeze({
        minZoom: data.minZoom,
        maxBounds: data.maxBounds,
        depotPos: data.depotPos,
        mapCenter: data.mapCenter,
        defaultZoom: data.defaultZoom,
        tileCount: data.tileCount,
        area: data.area,
        networkSize: data.networkSize
      })
    }
  },
  getters: {
    getCampaign: state => campaignID => state.campaigns.find(d => d._id === campaignID)
  },
  actions: {
    check ({ state, rootGetters }) {
      const clientInfo = rootGetters['device/clientInfo']

      return this.$api
        .getRevisions(clientInfo)
        .then(revisions => ({
          map: state.mapRevision >= revisions.map,
          activities: state.activitiesRevision >= revisions.activities,
          markers: state.markersRevision >= revisions.markers,
          campaigns: state.campaignsRevision >= revisions.campaigns,
          equipment: state.equipmentRevision >= revisions.equipment,
          fields: state.fieldsRevision >= revisions.fields,
          tracker: clientInfo.appVersion ? clientInfo.appVersion >= revisions.tracker : true
        }))
    },
    checkMap () {
      return this.$api.getMap()
    },
    getAll ({ commit, dispatch }, { doClear = false } = {}) {
      if (doClear) commit('clear')
      return Promise.all([
        dispatch('getCampaigns'),
        dispatch('getFields'),
        dispatch('getEquipment'),
        dispatch('getActivities'),
        dispatch('getMarkers')
      ])
    },
    getEquipment ({ commit, state }) {
      return this.$api.getEquipment(state.equipmentRevision).then(handleResult(commit, 'setEquipment'))
    },
    getActivities ({ commit, state }) {
      return this.$api.getActivities(state.activitiesRevision).then(handleResult(commit, 'setActivities'))
    },
    getMarkers ({ commit, state }) {
      return this.$api.getMarkers(state.markersRevision).then(handleResult(commit, 'setMarkers'))
    },
    getFields ({ commit, state }) {
      return this.$api.getFields(state.fieldsRevision).then(handleResult(commit, 'setFields'))
    },
    clearTiles () {
      return tileStore.clear()
    },
    getMap ({ commit, state }, { doClear = false, progressCallback } = {}) {
      if (doClear) commit('clearMap')

      return this.$api
        .getMap(state.mapRevision)
        .then(result => {
          if (result.code === 208) return 0

          if (result.code === 200) {
            commit('setMap', result)
            return tileStore.pull(result.data.tiles, this.$api, progressCallback).then(() => 1)
          }

          return -1
        })
    },
    getCampaigns ({ commit, state }) {
      return this.$api
        .getCampaigns(state.campaignsRevision)
        .then(handleResult(commit, 'setCampaigns'))
    },
    addCampaignEquipment ({ commit }, { campaignID, equipment }) {
      return this.$api
        .addCampaignEquipment({ campaignID, equipment })
        .then(({ data }) => commit('updateCampaign', data))
    }
  }
}

const enrichField = d => {
  return {
    ...d,
    __title: d.title || d.id,
    extentX: extent(d.coords, d => d[0]),
    extentY: extent(d.coords, d => d[1])
  }
}

const handleResult = (commit, mutation) => {
  return result => {
    if (result.code === 208) return 0

    if (result.code === 200) {
      commit(mutation, result)
      return 1
    }

    return -1
  }
}
